import React, { useEffect, useState } from 'react';
import TextInput from '../../widgets/TextInput';
import CheckboxInput from '../../widgets/CheckboxInput';
import { Customer } from '../../../models/Customer';
import { FaSave, FaTimes, FaTrash } from 'react-icons/fa';
import ContactsTab from '../Contacts/ContactsTab';
import GenericVerticalTabs, { TabConfig } from '../../common/GenericVerticalTabs/GenericVerticalTabs';
import ModalDialog from '../../common/ModalDialog/ModalDialog';
import './CustomerDialog.scss';
import { useTranslation } from 'react-i18next';

interface CustomerDialogProps {
  open: boolean;
  customer: Customer | null;
  onSave: (customer: Customer) => Promise<Customer>;
  onDelete: (customer: Customer) => void;
  onClose: () => void;
}

const emptyCustomer: Customer = {
  id: '',
  customerNumber: '',
  name: '',
  isCompany: false,
  addressLine1: '',
  addressLine2: '',
  city: '',
  zipCode: '',
  provinceOrState: '',
  country: '',
  phoneNumber: '',
  mobileNumber: '',
  emailAddress: '',
  btwId: '',
  kvkId: '',
  bankAccountNumber: '',
  useInvoiceAddress: false,
  invoiceAddressLine1: '',
  invoiceAddressLine2: '',
  invoiceZipCode: '',
  invoiceCity: '',
  invoiceProvinceOrState: '',
  invoiceCountry: '',
};

const sanitizeCustomer = (c: Partial<Customer>): Customer => {
  const merged: Customer = { ...emptyCustomer, ...c };
  (Object.keys(merged) as (keyof Customer)[]).forEach(k => {
    if (merged[k] === null) (merged as Record<keyof Customer, unknown>)[k] = '';
  });
  return merged;
};

const CustomerDialog: React.FC<CustomerDialogProps> = ({ open, customer, onSave, onDelete, onClose }) => {
  const { t } = useTranslation(['common', 'screens']);
  const [data, setData] = useState<Customer>(sanitizeCustomer(customer ?? {}));
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('details');

  useEffect(() => {
    setData(sanitizeCustomer(customer ?? {}));
    setShowDeleteConfirm(false);
    setActiveTab('details');
  }, [customer, open]);

  if (!open) return null;

  const isNew = !customer?.id;
  const set = <K extends keyof Customer>(field: K, value: Customer[K]) =>
    setData(prev => ({ ...prev, [field]: value }));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const saved = await onSave(data);
      setData(saved);
    } catch {
      // parent handles error display
    }
  };

  const tabs: TabConfig[] = [
    {
      key: 'details',
      label: t('screens:customerDialog.tabs.details'),
      content: (
        <div className="customer-dialog__details-form">
          <div className="customer-dialog__details-scroll">
            {/* Section: General */}
            <div className="dialog-section">
              <div className="dialog-section-title">{t('screens:customerDialog.sections.general')}</div>
              <div className="dialog-row">
                <div className="dialog-col dialog-col--wide">
                  <TextInput label={t('screens:customerDialog.fields.name')} value={data.name ?? ''} required onChange={v => set('name', v)} />
                </div>
                <div className="dialog-col">
                  <TextInput label={t('screens:customerDialog.fields.customerNumber')} value={data.customerNumber ?? ''} onChange={v => set('customerNumber', v)} />
                </div>
                <div className="customer-dialog__col--checkbox">
                  <CheckboxInput label={t('screens:customerDialog.fields.isCompany')} checked={data.isCompany} onChange={v => set('isCompany', v)} />
                </div>
              </div>
            </div>

            {/* Section: Address */}
            <div className="dialog-section">
              <div className="dialog-section-title">{t('screens:customerDialog.sections.address')}</div>
              <div className="dialog-row">
                <div className="dialog-col dialog-col--wide">
                  <TextInput label={t('screens:customerDialog.fields.addressLine1')} value={data.addressLine1 ?? ''} onChange={v => set('addressLine1', v)} />
                </div>
                <div className="dialog-col dialog-col--wide">
                  <TextInput label={t('screens:customerDialog.fields.addressLine2')} value={data.addressLine2 ?? ''} onChange={v => set('addressLine2', v)} />
                </div>
              </div>
              <div className="dialog-row">
                <div className="dialog-col dialog-col--wide">
                  <TextInput label={t('screens:customerDialog.fields.city')} value={data.city ?? ''} onChange={v => set('city', v)} />
                </div>
                <div className="dialog-col">
                  <TextInput label={t('screens:customerDialog.fields.zipCode')} value={data.zipCode ?? ''} onChange={v => set('zipCode', v)} />
                </div>
              </div>
              <div className="dialog-row">
                <div className="dialog-col">
                  <TextInput label={t('screens:customerDialog.fields.provinceState')} value={data.provinceOrState ?? ''} onChange={v => set('provinceOrState', v)} />
                </div>
                <div className="dialog-col">
                  <TextInput label={t('screens:customerDialog.fields.country')} value={data.country ?? ''} onChange={v => set('country', v)} />
                </div>
              </div>
            </div>

            {/* Section: Contact */}
            <div className="dialog-section">
              <div className="dialog-section-title">{t('screens:customerDialog.sections.contact')}</div>
              <div className="dialog-row">
                <div className="dialog-col">
                  <TextInput label={t('screens:customerDialog.fields.phoneNumber')} value={data.phoneNumber ?? ''} onChange={v => set('phoneNumber', v)} />
                </div>
                <div className="dialog-col">
                  <TextInput label={t('screens:customerDialog.fields.mobileNumber')} value={data.mobileNumber ?? ''} onChange={v => set('mobileNumber', v)} />
                </div>
                <div className="dialog-col dialog-col--wide">
                  <TextInput label={t('screens:customerDialog.fields.emailAddress')} value={data.emailAddress ?? ''} onChange={v => set('emailAddress', v)} />
                </div>
              </div>
            </div>

            {/* Section: Financial */}
            <div className="dialog-section">
              <div className="dialog-section-title">{t('screens:customerDialog.sections.financial')}</div>
              <div className="dialog-row">
                <div className="dialog-col">
                  <TextInput label={t('screens:customerDialog.fields.btwVatId')} value={data.btwId ?? ''} onChange={v => set('btwId', v)} />
                </div>
                <div className="dialog-col">
                  <TextInput label={t('screens:customerDialog.fields.kvkNumber')} value={data.kvkId ?? ''} onChange={v => set('kvkId', v)} />
                </div>
                <div className="dialog-col dialog-col--wide">
                  <TextInput label={t('screens:customerDialog.fields.bankAccountNumber')} value={data.bankAccountNumber ?? ''} onChange={v => set('bankAccountNumber', v)} />
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'invoicedetails',
      label: t('screens:customerDialog.tabs.invoiceDetails'),
      content: (
        <div className="customer-dialog__details-form">
          <div className="customer-dialog__details-scroll">
            <div className="dialog-section">
              <div className="dialog-section-title">{t('screens:customerDialog.sections.invoiceAddress')}</div>
              <div className="dialog-row">
                <div className="dialog-col dialog-col--full">
                  <CheckboxInput
                    label={t('screens:customerDialog.fields.useDifferentInvoiceAddress')}
                    checked={data.useInvoiceAddress}
                    onChange={v => set('useInvoiceAddress', v)}
                  />
                </div>
              </div>
              {data.useInvoiceAddress && (
                <>
                  <div className="dialog-row">
                    <div className="dialog-col dialog-col--wide">
                      <TextInput label={t('screens:customerDialog.fields.addressLine1')} value={data.invoiceAddressLine1 ?? ''} onChange={v => set('invoiceAddressLine1', v)} />
                    </div>
                    <div className="dialog-col dialog-col--wide">
                      <TextInput label={t('screens:customerDialog.fields.addressLine2')} value={data.invoiceAddressLine2 ?? ''} onChange={v => set('invoiceAddressLine2', v)} />
                    </div>
                  </div>
                  <div className="dialog-row">
                    <div className="dialog-col dialog-col--wide">
                      <TextInput label={t('screens:customerDialog.fields.city')} value={data.invoiceCity ?? ''} onChange={v => set('invoiceCity', v)} />
                    </div>
                    <div className="dialog-col">
                      <TextInput label={t('screens:customerDialog.fields.zipCode')} value={data.invoiceZipCode ?? ''} onChange={v => set('invoiceZipCode', v)} />
                    </div>
                  </div>
                  <div className="dialog-row">
                    <div className="dialog-col">
                      <TextInput label={t('screens:customerDialog.fields.provinceState')} value={data.invoiceProvinceOrState ?? ''} onChange={v => set('invoiceProvinceOrState', v)} />
                    </div>
                    <div className="dialog-col">
                      <TextInput label={t('screens:customerDialog.fields.country')} value={data.invoiceCountry ?? ''} onChange={v => set('invoiceCountry', v)} />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      ),
    },
    ...(!isNew ? [{ key: 'contacts', label: t('screens:customerDialog.tabs.contacts'), content: <ContactsTab parentId={data.id} parentType="Customer" /> }] : []),
  ];

  return (
    <ModalDialog
      open={open}
      title={isNew ? t('screens:customerDialog.newTitle') : t('screens:customerDialog.editTitle')}
      subtitle={!isNew ? customer?.name : undefined}
      onClose={onClose}
      onSubmit={handleSave}
      width={1024}
      minHeight={1024}
      bodyVariant="tabs"
      footerLeft={!isNew ? (
        showDeleteConfirm ? (
          <div className="customer-dialog__delete-confirm">
            <span className="customer-dialog__delete-confirm-text">{t('common:areYouSure')}</span>
            <button type="button" className="btn btn-danger btn-sm" onClick={() => onDelete(data)}>
              <FaTrash style={{ marginRight: 5 }} />{t('common:yesDelete')}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowDeleteConfirm(false)}>
              {t('common:cancel')}
            </button>
          </div>
        ) : (
          <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => setShowDeleteConfirm(true)}>
            <FaTrash style={{ marginRight: 5 }} />{t('common:delete')}
          </button>
        )
      ) : undefined}
      footerRight={
        <>
          <button type="submit" className="btn btn-primary">
            <FaSave style={{ marginRight: 5 }} />{t('common:save')}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            <FaTimes style={{ marginRight: 5 }} />{t('common:close')}
          </button>
        </>
      }
    >
      <GenericVerticalTabs
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        className="customer-dialog__tabs"
      />
    </ModalDialog>
  );
};

export default CustomerDialog;
